
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Video, ExternalLink } from 'lucide-react';

interface VideoPlayerProps {
  url: string;
  title?: string;
  className?: string;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ url, title, className = '' }) => {
  const getVideoEmbedUrl = (url: string) => {
    try {
      // YouTube URL conversion
      if (url.includes('youtube.com/watch') || url.includes('youtu.be/')) {
        const videoId = url.includes('youtu.be/') 
          ? url.split('youtu.be/')[1]?.split('?')[0]
          : new URLSearchParams(new URL(url).search).get('v');
        return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
      }
      
      // Vimeo URL conversion
      if (url.includes('vimeo.com')) {
        const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
        return videoId ? `https://player.vimeo.com/video/${videoId}` : null;
      }
      
      return null;
    } catch (error) {
      console.error('Error parsing video URL:', error);
      return null;
    }
  };

  const embedUrl = getVideoEmbedUrl(url);

  if (!embedUrl) {
    return (
      <Card className={`bg-gray-800 border-gray-700 ${className}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Video className="h-4 w-4 text-sports-green" />
              <span className="text-white text-sm">{title || 'Video Link'}</span>
            </div>
            <a 
              href={url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-sports-green hover:text-green-400 text-sm"
            >
              <ExternalLink className="h-3 w-3" />
              Open
            </a>
          </div>
          <p className="text-gray-400 text-xs mt-2 truncate">{url}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={`relative w-full ${className}`}>
      <div className="relative w-full h-0 pb-[56.25%]"> {/* 16:9 aspect ratio */}
        <iframe
          src={embedUrl}
          title={title || 'Video Player'}
          className="absolute top-0 left-0 w-full h-full rounded-lg"
          allowFullScreen
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        />
      </div>
      {title && (
        <p className="text-white text-sm mt-2 text-center">{title}</p>
      )}
    </div>
  );
};

export default VideoPlayer;
